import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Mail, MoonStar } from "lucide-react";
import { toast } from "sonner";

export function AccountSettings({ user }) {
  const [notify, setNotify] = useState(user?.notify_on_complete !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/account/settings").then(({ data }) => setNotify(data.notify_on_complete)).catch(() => {});
  }, []);

  const toggle = async (next) => {
    setNotify(next);
    setSaving(true);
    try {
      const { data } = await api.patch("/account/settings", { notify_on_complete: next });
      setNotify(data.notify_on_complete);
      toast.success(next ? "We'll email you when each batch finishes." : "Completion emails turned off.");
    } catch {
      setNotify(!next);
      toast.error("Could not save that setting. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-settings" className="max-w-3xl space-y-6">
      <div className="rounded-2xl border border-surface-800 bg-panel p-6">
        <h2 className="font-display text-2xl font-bold mb-1">Account</h2>
        <p data-testid="account-email" className="text-sm text-copy-500">{user.email}</p>
      </div>

      <div className="rounded-2xl border border-surface-800 bg-panel p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center"><MoonStar className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold">Background processing</h3>
            <p data-testid="background-processing-explainer" className="text-sm text-copy-400 mt-1 leading-relaxed">Once your upload is saved, clips are created on our servers. You can close the tab or shut your laptop; if the processor restarts, your batch continues from where it stopped.</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-surface-800 bg-surface-950 p-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-copy-400 mt-0.5" />
            <div>
              <Label htmlFor="notify-complete" className="text-sm font-medium">Email me when a batch finishes</Label>
              <p className="text-xs text-copy-500 mt-1">One email per batch, sent to {user.email}, when your clips are ready or if a batch stops early. No marketing.</p>
            </div>
          </div>
          <Switch id="notify-complete" data-testid="notify-complete-switch" checked={notify} onCheckedChange={toggle} disabled={saving} className="data-[state=checked]:bg-cyan-500" />
        </div>
      </div>
    </div>
  );
}
